import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ShieldCheck, Loader2, CheckCircle, AlertCircle, MinusCircle,
  CalendarClock, FileText, Link2, FileCheck2, ClipboardCheck,
  BookOpenCheck, GraduationCap, Fingerprint, ScrollText, ChevronDown,
  FileSignature,
} from 'lucide-react';
import { complianceApi, authorizedFetch } from '../utils/api';
import SecurityReportsList from '../components/SecurityReportsList';

// Chip styling per pillar status — matches the badge palette used across
// the portal (Dashboard/Billing status configs).
const PILLAR_STATUS = {
  present: { label: 'In place', color: '#16a34a', bg: '#dcfce7', icon: CheckCircle },
  partial: { label: 'Partial', color: '#b45309', bg: '#fef3c7', icon: AlertCircle },
  gap:     { label: 'Gap',     color: '#b91c1c', bg: '#fee2e2', icon: MinusCircle },
};

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch (e) { return iso; }
}

// Chip styling per BAA status — same badge palette as PILLAR_STATUS.
const BAA_STATUS = {
  signed:     { label: 'Signed',     color: '#16a34a', bg: '#dcfce7' },
  active:     { label: 'Active',     color: '#16a34a', bg: '#dcfce7' },
  pending:    { label: 'Pending',    color: '#b45309', bg: '#fef3c7' },
  expired:    { label: 'Expired',    color: '#b91c1c', bg: '#fee2e2' },
  terminated: { label: 'Terminated', color: '#b91c1c', bg: '#fee2e2' },
};

function baaStatusCfg(status) {
  if (typeof status === 'string' && BAA_STATUS[status.toLowerCase()]) {
    return BAA_STATUS[status.toLowerCase()];
  }
  const label = typeof status === 'string' && status
    ? status.charAt(0).toUpperCase() + status.slice(1)
    : 'Unknown';
  return { label, color: '#555', bg: '#eee' };
}

// Amber/red expiry chip when a BAA is expired or expiring within 60 days.
function baaExpiryChip(baa) {
  const days = typeof baa.expires_in_days === 'number' && Number.isFinite(baa.expires_in_days)
    ? Math.trunc(baa.expires_in_days)
    : null;
  if (days == null) return null;
  if (days < 0) return { label: 'Expired', color: '#b91c1c', bg: '#fee2e2' };
  if (days === 0) return { label: 'Expires today', color: '#b91c1c', bg: '#fee2e2' };
  if (days <= 60) {
    return { label: `Expires in ${days} day${days === 1 ? '' : 's'}`, color: '#b45309', bg: '#fef3c7' };
  }
  return null;
}

const cardStyle = {
  background: '#fff', border: '1px solid #eee', borderRadius: 10,
  padding: '16px 18px', boxShadow: '0 1px 3px rgba(0,0,0,.05)',
};


/* ── MSA / BAA documents ────────────────────────────────────────────── */

// Shown at the top of the HIPAA tab for BOTH states — an enrolled client and
// one who's only being introduced to the service. A pending package is the
// call to action; an executed BAA becomes the expiry line under compliance
// status. Renders nothing when there's neither, so it never adds noise.
function HipaaDocuments({ docs }) {
  if (!docs) return null;
  const pending = docs.pending_package;
  const baa = docs.baa;
  if (!pending && !baa) return null;

  const expiry = baa ? baaExpiryChip({ expires_in_days: baa.expires_in_days }) : null;

  return (
    <>
      {pending && (
        <div style={{
          ...cardStyle, marginBottom: 14, borderColor: '#068dac',
          background: 'linear-gradient(0deg, #f6fdff, #fff)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <FileSignature size={22} color="#068dac" style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#05708a' }}>
                {pending.remaining === 1
                  ? '1 document is waiting for your signature'
                  : `${pending.remaining} documents are waiting for your signature`}
              </div>
              <div style={{ fontSize: 13, color: '#666', marginTop: 3 }}>
                {pending.documents.map((d) => d.name).join(' · ')}
              </div>
            </div>
            <a
              href={`/sign/${pending.package_token}`}
              style={{
                background: '#068dac', color: '#fff', textDecoration: 'none',
                borderRadius: 8, padding: '10px 18px', fontSize: 14, fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              Review &amp; Sign
            </a>
          </div>
          <p style={{ fontSize: 12.5, color: '#777', margin: '10px 0 0' }}>
            Your Business Associate Agreement is what HIPAA requires between your
            practice and any vendor that handles PHI — signing it here puts the
            executed copy on file for both of us.
          </p>
        </div>
      )}

      {baa && (
        <div style={{ ...cardStyle, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <ScrollText size={22} color={expiry ? '#b45309' : '#2f7d4f'} style={{ flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Business Associate Agreement</div>
            <div style={{ fontSize: 13, color: '#777', marginTop: 2 }}>
              Signed {fmtDate(baa.signed_at)} · Expires <strong>{fmtDate(baa.expires_at)}</strong>
            </div>
          </div>
          {expiry ? (
            <span style={{
              padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600,
              color: expiry.color, background: expiry.bg, whiteSpace: 'nowrap',
            }}>{expiry.label}</span>
          ) : (
            <span style={{
              padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600,
              color: '#15803d', background: '#dcfce7', whiteSpace: 'nowrap',
            }}>Current</span>
          )}
        </div>
      )}
    </>
  );
}

/* ── ACTIVE: real dashboard ─────────────────────────────────────────── */

// One pillar's expanded body — "What's in place" / "What's needed" lists in
// plain client-facing language, using the same green/amber/red tokens as the
// status chips above.
function PillarDetailBody({ detail, status }) {
  if (!detail) {
    return <div style={{ fontSize: 13, color: '#888' }}>Detail unavailable.</div>;
  }
  const present = Array.isArray(detail.present) ? detail.present : [];
  const gaps = Array.isArray(detail.gaps) ? detail.gaps : [];
  const gapColor = (detail.status || status) === 'gap' ? '#b91c1c' : '#b45309';
  const sectionTitle = {
    fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase',
    letterSpacing: 0.4, margin: '0 0 6px',
  };
  const bulletRow = { display: 'flex', alignItems: 'flex-start', gap: 7, fontSize: 13.5, lineHeight: 1.5, color: '#333', marginBottom: 4 };

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <div style={sectionTitle}>What's in place</div>
        {present.length > 0 ? present.map((item, idx) => (
          <div key={idx} style={bulletRow}>
            <CheckCircle size={14} color="#16a34a" style={{ flexShrink: 0, marginTop: 3 }} />
            <span>{item}</span>
          </div>
        )) : (
          <div style={{ fontSize: 13, color: '#888' }}>Nothing on file yet.</div>
        )}
      </div>
      <div>
        <div style={sectionTitle}>What's needed</div>
        {gaps.length > 0 ? gaps.map((item, idx) => (
          <div key={idx} style={bulletRow}>
            <AlertCircle size={14} color={gapColor} style={{ flexShrink: 0, marginTop: 3 }} />
            <span>{item}</span>
          </div>
        )) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#16a34a', fontWeight: 600 }}>
            <CheckCircle size={14} />
            All requirements met.
          </div>
        )}
      </div>
    </>
  );
}

function ComplianceDashboard({ data }) {
  const navigate = useNavigate();
  const s = data.summary || {};
  const pillars = s.pillars || [];

  // Per-pillar drill-down: which rows are open, plus a single lazily-fetched
  // detail payload (fetched once on the first expand, cached in state).
  const [expandedRows, setExpandedRows] = useState({});
  const [detailState, setDetailState] = useState('idle'); // idle | loading | ready | error
  const [pillarDetails, setPillarDetails] = useState([]);

  const loadPillarDetails = async () => {
    if (detailState !== 'idle') return; // already fetched / in flight
    setDetailState('loading');
    try {
      const res = await authorizedFetch('/api/portal/compliance/pillars');
      if (!res.ok) throw new Error('Failed to load pillar detail');
      const d = await res.json();
      setPillarDetails(Array.isArray(d.pillars) ? d.pillars : []);
      setDetailState('ready');
    } catch (e) {
      setDetailState('error');
    }
  };

  const toggleRow = (rowKey) => {
    setExpandedRows((prev) => {
      const opening = !prev[rowKey];
      if (opening) loadPillarDetails();
      return { ...prev, [rowKey]: opening };
    });
  };

  // Match a summary pillar (key/label) to its detail entry — by key when
  // both sides have one, otherwise tolerant case-insensitive name match.
  const findPillarDetail = (p) => {
    if (!pillarDetails.length) return null;
    if (p.key) {
      const byKey = pillarDetails.find((d) => d.key === p.key);
      if (byKey) return byKey;
    }
    const label = String(p.label || p.name || '').trim().toLowerCase();
    if (!label) return null;
    return pillarDetails.find((d) =>
      String(d.name || '').trim().toLowerCase() === label
      || String(d.key || '').trim().toLowerCase() === label
    ) || null;
  };
  const dueSoon = s.obligations_due_soon || 0;
  const overdue = s.obligations_overdue || 0;
  const baas = s.baas_expiring || 0;
  const baaList = Array.isArray(s.baas) ? s.baas.filter((b) => b && typeof b === 'object') : [];
  const report = s.latest_clean_report;

  return (
    <>
      <p style={{ color: '#777', fontSize: 14, margin: '0 0 20px' }}>
        {data.service_label || 'Your compliance evidence and audit readiness, at a glance.'}
      </p>

      {/* Posture summary */}
      <div style={{ ...cardStyle, display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'center', marginBottom: 14 }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '6px 12px', borderRadius: 999, fontSize: 13, fontWeight: 600,
          color: s.chain_ok ? '#16a34a' : '#b91c1c',
          background: s.chain_ok ? '#dcfce7' : '#fee2e2',
        }}>
          {s.chain_ok ? <ShieldCheck size={15} /> : <AlertCircle size={15} />}
          {s.chain_ok ? 'Evidence chain intact' : 'Evidence chain needs attention'}
        </div>
        <div style={{ fontSize: 13, color: '#555' }}>
          <span style={{ color: '#888' }}>Last evidence:</span>{' '}
          <strong>{fmtDate(s.last_evidence_at)}</strong>
        </div>
        <div style={{ fontSize: 13, color: '#555' }}>
          <span style={{ color: '#888' }}>Evidence records:</span>{' '}
          <strong>{s.evidence_count != null ? s.evidence_count.toLocaleString() : '—'}</strong>
        </div>
      </div>

      {/* Obligations + BAA callouts */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 14 }}>
        <div style={{ ...cardStyle, flex: '1 1 220px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <CalendarClock size={26} color={overdue > 0 ? '#b91c1c' : '#068dac'} style={{ flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>
              {dueSoon} obligation{dueSoon === 1 ? '' : 's'} due soon
            </div>
            <div style={{ fontSize: 13, color: overdue > 0 ? '#b91c1c' : '#888', fontWeight: overdue > 0 ? 600 : 400 }}>
              {overdue > 0 ? `${overdue} overdue — needs attention` : 'Nothing overdue'}
            </div>
          </div>
        </div>
        <div style={{ ...cardStyle, flex: '1 1 220px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <ScrollText size={26} color={baas > 0 ? '#b45309' : '#068dac'} style={{ flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>
              {baas} BAA{baas === 1 ? '' : 's'} expiring
            </div>
            <div style={{ fontSize: 13, color: '#888' }}>
              {baas > 0 ? 'Renewals coming up on your Business Associate Agreements' : 'All Business Associate Agreements current'}
            </div>
          </div>
        </div>
      </div>

      {/* Pillars checklist */}
      <div style={{ ...cardStyle, marginBottom: 14 }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Compliance pillars</div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {pillars.map((p, i) => {
            const cfg = PILLAR_STATUS[p.status] || PILLAR_STATUS.partial;
            const Icon = cfg.icon;
            const rowKey = p.key || p.label || String(i);
            const isOpen = !!expandedRows[rowKey];
            return (
              <div
                key={rowKey}
                style={{ borderTop: i === 0 ? 'none' : '1px solid #f2f2f2' }}
              >
                <button
                  type="button"
                  onClick={() => toggleRow(rowKey)}
                  aria-expanded={isOpen}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: 12, padding: '10px 0', width: '100%',
                    background: 'none', border: 'none', cursor: 'pointer',
                    font: 'inherit', textAlign: 'left',
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <ChevronDown
                      size={15}
                      color="#888"
                      style={{
                        flexShrink: 0,
                        transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                        transition: 'transform .15s ease',
                      }}
                    />
                    <span style={{ fontSize: 14, color: '#333' }}>{p.label}</span>
                  </span>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0,
                    padding: '3px 10px', borderRadius: 999,
                    fontSize: 12, fontWeight: 600, color: cfg.color, background: cfg.bg,
                  }}>
                    <Icon size={13} />
                    {cfg.label}
                  </span>
                </button>
                {isOpen && (
                  <div style={{ padding: '2px 0 14px 23px' }}>
                    {detailState === 'loading' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#888' }}>
                        <Loader2 size={13} className="spinner" />
                        Loading detail…
                      </div>
                    )}
                    {detailState === 'error' && (
                      <div style={{ fontSize: 13, color: '#888' }}>Detail unavailable.</div>
                    )}
                    {detailState === 'ready' && (
                      <PillarDetailBody detail={findPillarDetail(p)} status={p.status} />
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {pillars.length === 0 && (
            <div style={{ fontSize: 13, color: '#888', padding: '6px 0' }}>
              Pillar assessment in progress — check back soon.
            </div>
          )}
        </div>
      </div>

      {/* Business Associate Agreements */}
      <div style={{ ...cardStyle, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <ScrollText size={17} color="#068dac" />
          <div style={{ fontSize: 15, fontWeight: 700 }}>Business Associate Agreements</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {baaList.map((b, i) => {
            const cfg = baaStatusCfg(b.status);
            const expiry = baaExpiryChip(b);
            return (
              <div
                key={`${b.vendor_name || 'baa'}-${i}`}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  flexWrap: 'wrap', gap: 10, padding: '10px 0',
                  borderTop: i === 0 ? 'none' : '1px solid #f2f2f2',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#333' }}>
                    {b.vendor_name || 'Unnamed agreement'}
                    {b.direction === 'incoming' && (
                      <span style={{ fontSize: 12, fontWeight: 400, color: '#888', marginLeft: 8 }}>
                        with Macula
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12.5, color: '#888', marginTop: 2 }}>
                    Signed {fmtDate(b.signed_at)} · Expires {fmtDate(b.expires_at)}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center',
                    padding: '3px 10px', borderRadius: 999,
                    fontSize: 12, fontWeight: 600, color: cfg.color, background: cfg.bg,
                  }}>
                    {cfg.label}
                  </span>
                  {expiry && (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '3px 10px', borderRadius: 999,
                      fontSize: 12, fontWeight: 600, color: expiry.color, background: expiry.bg,
                    }}>
                      <CalendarClock size={13} />
                      {expiry.label}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
          {baaList.length === 0 && (
            <div style={{ fontSize: 13, color: '#888', padding: '6px 0' }}>
              No Business Associate Agreements on file yet.
            </div>
          )}
        </div>
      </div>

      {/* Latest report */}
      <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: 14 }}>
        <FileText size={26} color="#068dac" style={{ flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          {report ? (
            <>
              <div style={{ fontSize: 15, fontWeight: 600 }}>
                Latest compliance report{report.period ? ` — ${report.period}` : ''}
              </div>
              <div style={{ fontSize: 13, color: '#888' }}>
                {fmtDate(report.generated_at)} ·{' '}
                <span style={{ color: report.clean ? '#16a34a' : '#b45309', fontWeight: 600 }}>
                  {report.clean ? 'Clean' : 'Needs attention'}
                </span>
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 15, fontWeight: 600 }}>No compliance report yet</div>
              <div style={{ fontSize: 13, color: '#888' }}>
                Your first report will appear here once generated.
              </div>
            </>
          )}
        </div>
        <button
          onClick={() => navigate('/reports')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'none', border: '1px solid #068dac', borderRadius: 8,
            color: '#068dac', padding: '8px 14px', fontSize: 13, fontWeight: 600,
            cursor: 'pointer', flexShrink: 0,
          }}
        >
          <Link2 size={14} />
          View Reports
        </button>
      </div>
    </>
  );
}

/* ── INACTIVE: sales pitch ──────────────────────────────────────────── */

const PITCH_ITEMS = [
  {
    icon: ClipboardCheck,
    title: 'Security Risk Analysis',
    body: 'The formal SRA that HIPAA actually requires — done, documented, and refreshed on schedule.',
  },
  {
    icon: BookOpenCheck,
    title: 'Policies that match reality',
    body: 'A maintained policy set mapped to the HIPAA Security Rule — not a binder of templates nobody follows.',
  },
  {
    icon: ScrollText,
    title: 'BAA register with expiry alerts',
    body: 'Every Business Associate Agreement tracked in one place, with a heads-up before any of them lapse.',
  },
  {
    icon: GraduationCap,
    title: 'Workforce training records',
    body: 'Who was trained, on what, and when — pulled up in seconds instead of chased down for weeks.',
  },
  {
    icon: Fingerprint,
    title: 'Tamper-evident evidence trail',
    body: 'Every piece of evidence is hash-chained the moment it lands. Nothing backfilled, nothing quietly edited — auditors can verify it themselves.',
  },
];

function CompliancePitch() {
  const [ctaState, setCtaState] = useState('idle'); // idle | sending | sent | error

  const handleInterest = async () => {
    setCtaState('sending');
    try {
      await complianceApi.expressInterest();
      setCtaState('sent');
    } catch (e) {
      setCtaState('error');
    }
  };

  return (
    <>
      <div style={{ textAlign: 'center', padding: '18px 0 26px' }}>
        <ShieldCheck size={44} color="#068dac" style={{ marginBottom: 14 }} />
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: '0 0 12px' }}>HIPAA Compliance</h1>
        <p style={{ fontSize: 17, color: '#333', lineHeight: 1.5, margin: '0 auto 10px', maxWidth: 560, fontWeight: 500 }}>
          When an auditor comes knocking, hand them a report — instead of
          panicking for three weeks.
        </p>
        <p style={{ fontSize: 14, color: '#777', lineHeight: 1.6, margin: '0 auto', maxWidth: 560 }}>
          Most practices are doing the right things. Almost none can prove it.
          Our HIPAA compliance service builds and maintains your complete audit
          binder as a service — the evidence, not just the intentions — so the
          proof exists before anyone asks for it.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 26 }}>
        {PITCH_ITEMS.map((item) => {
          const Icon = item.icon;
          return (
            <div key={item.title} style={{ ...cardStyle, display: 'flex', gap: 14, alignItems: 'flex-start' }}>
              <Icon size={22} color="#068dac" style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 2 }}>{item.title}</div>
                <div style={{ fontSize: 13.5, color: '#777', lineHeight: 1.55 }}>{item.body}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ ...cardStyle, textAlign: 'center', padding: '26px 18px' }}>
        {ctaState === 'sent' ? (
          <>
            <FileCheck2 size={32} color="#16a34a" style={{ marginBottom: 10 }} />
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Thanks — we'll be in touch.</div>
            <p style={{ fontSize: 13.5, color: '#777', margin: 0 }}>
              Someone from Macula will reach out shortly to walk you through
              what compliance-as-a-service looks like for your practice.
            </p>
          </>
        ) : (
          <>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
              Ready to stop hoping and start proving?
            </div>
            <p style={{ fontSize: 13.5, color: '#777', margin: '0 0 16px' }}>
              No commitment — we'll show you exactly what your audit binder
              would look like.
            </p>
            <button
              onClick={handleInterest}
              disabled={ctaState === 'sending'}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: '#068dac', color: '#fff', border: 'none', borderRadius: 8,
                padding: '12px 24px', fontSize: 14, fontWeight: 600,
                cursor: ctaState === 'sending' ? 'default' : 'pointer',
                opacity: ctaState === 'sending' ? 0.7 : 1,
              }}
            >
              {ctaState === 'sending' && <Loader2 size={15} className="spinner" />}
              I'm interested — talk to us
            </button>
            {ctaState === 'error' && (
              <div style={{ fontSize: 13, color: '#c0392b', marginTop: 10 }}>
                Something went wrong sending your request — please try again,
                or just give us a call.
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

/* ── PAGE ───────────────────────────────────────────────────────────── */

/**
 * Compliance page — visible to every portal customer.
 * Active HIPAA compliance service -> live posture dashboard.
 * No active service -> pitch for compliance-evidence-as-a-service.
 */
export default function Compliance() {
  const navigate = useNavigate();
  const location = useLocation();
  // ?tab=reports lets the old /reports redirect (and emailed links) land
  // straight on the assessments list.
  const [tab, setTab] = useState(
    new URLSearchParams(location.search).get('tab') === 'reports' ? 'reports' : 'status'
  );
  const [data, setData] = useState(null);
  const [docs, setDocs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await complianceApi.getStatus();
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setError('Could not load compliance information.');
      } finally {
        if (!cancelled) setLoading(false);
      }
      // Best-effort: the MSA/BAA block just doesn't render if this fails.
      try {
        const hd = await complianceApi.getHipaaDocuments();
        if (!cancelled) setDocs(hd);
      } catch (e) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // One HIPAA home: compliance status and the security assessments that back
  // it, as tabs. (The old standalone /reports page now redirects here.)
  const TABS = [
    { key: 'status',  label: 'Compliance Status' },
    { key: 'reports', label: 'Security Reports' },
  ];

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '24px 16px' }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 14px' }}>HIPAA</h1>

      <div className="hipaa-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`hipaa-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'reports' ? (
        <SecurityReportsList />
      ) : (
        <>
          {loading && (
            <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>
              <Loader2 className="spinner-large" size={36} />
              <div style={{ marginTop: 10 }}>Loading…</div>
            </div>
          )}

          {!loading && error && (
            <div style={{ padding: 30, color: '#c0392b' }}>{error}</div>
          )}

          {!loading && <HipaaDocuments docs={docs} />}

          {!loading && !error && data && (
            data.active ? <ComplianceDashboard data={data} /> : <CompliancePitch />
          )}
        </>
      )}
    </div>
  );
}
